/**
 * DispenserScene — 28 포켓 3D 디스펜서 씬 (DeviceDetailModal 의 <Canvas> 내부 전용).
 *
 * pocket_index = day_of_week(0=월..6=일) * 4 + slot(아침0/점심1/저녁2/취침3).
 * 포켓 3-상태: 채움(약+슬롯색) / 복용완료(초록·약없음) / 비활성(처방 없는 빈 슬롯·회색).
 * 채워진 활성 포켓이 복용 시간대 + 오늘 칸이면 셀 테두리 LED 가 점멸한다.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { OrbitControls, RoundedBox, ContactShadows, Environment, Float, Html } from '@react-three/drei'
import * as THREE from 'three'

// 슬롯별 색상 (아침/점심/저녁/취침) — 약병 액체 톤 + LED 점멸 강조색
export const SLOT_COLOR = ['#7fcfb5', '#f5c97a', '#c8a4d6', '#8ec1e0']
const LED_COLOR = ['#36e0a0', '#ffb938', '#cf7af0', '#3fb6ff']
const DAY_LABEL = ['월', '화', '수', '목', '금', '토', '일']

// 포켓 복용 상태별 박스색 — 주차 조회(전주/다음주 약달력)에서 복용 상태를 색으로 표현.
//   pending=슬롯색(복용대기·알약표시) / taken=초록 / missed=빨강 / late=주황.
const STATUS_BOX: Record<string, string> = { taken: '#52c41a', missed: '#ff4d4f', late: '#fa8c16' }
// 해당 포켓이 '복용 대기(알약 있음)' 상태인지 — status 우선, 구버전 응답(status 없음)은 filled 폴백.
export const isPendingPocket = (info: { status?: string | null; filled: boolean }) =>
  info.status ? info.status === 'pending' : info.filled

export interface PocketInfo {
  filled: boolean
  active: boolean
  medName: string | null
  dose: number | null
  status?: string | null   // pending|taken|late|missed — 대상 주의 복용 상태(약달력 색)
}

export interface DispenserSceneProps {
  pockets: Record<number, PocketInfo>
  activeSlot: number
  todayCol: number
  slotLabels: string[]
  isCurrentWeek?: boolean   // 이번주만 LED 점멸(due 안내) — 전주/다음주는 정적 약달력
  onPocketClick: (idx: number) => void
  onHover: (idx: number | null) => void
}

function Pill() {
  return (
    <Float speed={2.4} rotationIntensity={0.18} floatIntensity={0.22}>
      <mesh position={[0, 0.32, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <capsuleGeometry args={[0.13, 0.26, 8, 16]} />
        <meshPhysicalMaterial color="#ffffff" metalness={0.05} roughness={0.35} clearcoat={0.7} clearcoatRoughness={0.2} />
      </mesh>
    </Float>
  )
}

// 포켓 상단 테두리를 감싸는 얇은 LED 파이프 — blink=true 일 때 점멸
function LedFrame({ color, blink }: { color: string; blink: boolean }) {
  const mat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#16191f',
        emissive: new THREE.Color(color),
        emissiveIntensity: 0.045,
        metalness: 0.4,
        roughness: 0.35,
        toneMapped: false,
      }),
    [color],
  )
  useEffect(() => () => mat.dispose(), [mat])
  useFrame((state) => {
    const tt = state.clock.elapsedTime
    mat.emissiveIntensity = blink ? 0.1 + 1.7 * (0.5 + 0.5 * Math.sin(tt * 6.5)) : 0.045
  })
  const bars = [
    { args: [1.0, 0.045, 0.055], pos: [0, 0, 0.485] },
    { args: [1.0, 0.045, 0.055], pos: [0, 0, -0.485] },
    { args: [0.055, 0.045, 1.0], pos: [0.485, 0, 0] },
    { args: [0.055, 0.045, 1.0], pos: [-0.485, 0, 0] },
  ] as const
  return (
    <group position={[0, 0.185, 0]}>
      {bars.map((b, i) => (
        <mesh key={i} position={b.pos as [number, number, number]} material={mat}>
          <boxGeometry args={b.args as [number, number, number]} />
        </mesh>
      ))}
    </group>
  )
}

// 디스펜서 가장자리 텍스트 라벨 (요일 / 슬롯) — 한글 지원 위해 drei Html 사용
function EdgeLabel({ position, text, highlight }: { position: [number, number, number]; text: string; highlight?: boolean }) {
  return (
    <Html position={position} center zIndexRange={[10, 0]} style={{ pointerEvents: 'none' }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          padding: '1px 7px',
          borderRadius: 7,
          color: highlight ? '#fff' : '#3a4250',
          background: highlight ? '#1677ff' : 'rgba(255,255,255,0.88)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.22)',
        }}
      >
        {text}
      </div>
    </Html>
  )
}

function Pocket({
  position, slotColor, ledColor, idx, info, blink, onClick, onHover,
}: {
  position: [number, number, number]
  slotColor: string
  ledColor: string
  idx: number
  info: PocketInfo
  blink: boolean
  onClick: (idx: number) => void
  onHover: (idx: number | null) => void
}) {
  const meshRef = useRef<THREE.Group>(null)
  const [hovered, setHovered] = useState(false)
  const { active } = info
  const pending = isPendingPocket(info)          // 복용대기(알약 표시) 여부
  const boxColor = pending ? slotColor : (STATUS_BOX[info.status ?? 'taken'] ?? '#52c41a')

  useFrame((_, delta) => {
    if (!meshRef.current) return
    const targetY = position[1] + (hovered ? 0.08 : 0) - (pending ? 0 : 0.04)
    meshRef.current.position.y += (targetY - meshRef.current.position.y) * Math.min(1, delta * 8)
  })

  // 비활성 — 처방 일정이 없는 빈 슬롯 (회색 함몰, 상호작용 없음)
  if (!active) {
    return (
      <group position={position}>
        <RoundedBox args={[0.92, 0.2, 0.92]} radius={0.06} smoothness={3} position={[0, -0.07, 0]} receiveShadow>
          <meshPhysicalMaterial color="#9aa3af" metalness={0.1} roughness={0.85} />
        </RoundedBox>
      </group>
    )
  }

  // 활성 — 상태별 색(대기=슬롯색+알약 / 복용=초록 / 미복용=빨강 / 지연=주황)
  return (
    <group
      ref={meshRef}
      position={position}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); onHover(idx); document.body.style.cursor = 'pointer' }}
      onPointerOut={() => { setHovered(false); onHover(null); document.body.style.cursor = 'default' }}
      onClick={(e) => { e.stopPropagation(); onClick(idx) }}
    >
      <RoundedBox args={[0.92, 0.34, 0.92]} radius={0.08} smoothness={4} castShadow receiveShadow>
        <meshPhysicalMaterial
          color={boxColor}
          metalness={0.15}
          roughness={0.45}
          clearcoat={0.4}
          clearcoatRoughness={0.6}
          emissive={pending ? '#000000' : boxColor}
          emissiveIntensity={pending ? 0 : 0.22}
        />
      </RoundedBox>
      <LedFrame color={ledColor} blink={blink} />
      {pending && <Pill />}
    </group>
  )
}

function Dispenser({ pockets, activeSlot, todayCol, slotLabels, isCurrentWeek = true, onPocketClick, onHover }: DispenserSceneProps) {
  const cells: JSX.Element[] = []
  for (let day = 0; day < 7; day++) {
    for (let slot = 0; slot < 4; slot++) {
      const idx = day * 4 + slot
      const info = pockets[idx] ?? { filled: false, active: false, medName: null, dose: null }
      cells.push(
        <Pocket
          key={idx}
          position={[(slot - 1.5) * 1.05, 0, (day - 3) * 1.05]}
          slotColor={SLOT_COLOR[slot]}
          ledColor={LED_COLOR[slot]}
          idx={idx}
          info={info}
          // 이번주의 '지금 복용할' 대기 포켓만 점멸 — 전주/다음주는 정적 약달력
          blink={isCurrentWeek && info.active && isPendingPocket(info) && slot === activeSlot && day === todayCol}
          onClick={onPocketClick}
          onHover={onHover}
        />,
      )
    }
  }

  return (
    <>
      {/* 디스펜서 본체 (베이스) — 행 7(요일) × 열 4(슬롯) */}
      <RoundedBox args={[5.2, 0.5, 8.4]} radius={0.18} smoothness={4} position={[0, -0.45, 0]} castShadow receiveShadow>
        <meshPhysicalMaterial color="#2c3138" metalness={0.35} roughness={0.55} clearcoat={0.3} />
      </RoundedBox>
      {/* 상판 */}
      <RoundedBox args={[4.8, 0.12, 8.0]} radius={0.1} smoothness={4} position={[0, -0.16, 0]} receiveShadow>
        <meshPhysicalMaterial color="#e2e6eb" metalness={0.1} roughness={0.7} />
      </RoundedBox>
      {/* 슬롯 라벨 (뒤쪽 가장자리) — 컬럼 헤더 아침→취침, 현재 복용 시간대 강조 */}
      {slotLabels.map((s, i) => (
        <EdgeLabel key={i} position={[(i - 1.5) * 1.05, 0.02, -3.95]} text={s} highlight={i === activeSlot} />
      ))}
      {/* 요일 라벨 (왼쪽 가장자리) — 행 헤더 월→일, 오늘 칸 강조 */}
      {DAY_LABEL.map((d, i) => (
        <EdgeLabel key={d} position={[-2.55, 0.02, (i - 3) * 1.05]} text={d} highlight={i === todayCol} />
      ))}
      {cells}
    </>
  )
}

/** <Canvas> 내부 씬 루트 — 조명 + 디스펜서 + 그림자 + 카메라 컨트롤. */
export default function DispenserScene(props: DispenserSceneProps) {
  return (
    <>
      <ambientLight intensity={0.35} />
      <directionalLight
        position={[6, 10, 6]}
        intensity={1.1}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-left={-8}
        shadow-camera-right={8}
        shadow-camera-top={8}
        shadow-camera-bottom={-8}
      />
      <directionalLight position={[-5, 4, -3]} intensity={0.35} color="#bcdfff" />
      <Environment preset="apartment" />
      <Dispenser {...props} />
      <ContactShadows position={[0, -0.69, 0]} opacity={0.45} scale={14} blur={2.4} far={4} />
      <OrbitControls
        enablePan={false}
        maxPolarAngle={Math.PI / 2.2}
        minDistance={7}
        maxDistance={22}
        enableDamping
        dampingFactor={0.08}
      />
    </>
  )
}
